import { z } from 'zod';
import { PatientIdSchema } from './common';

/**
 * Constancia de entrega de resultado de patología. Documento por PACIENTE
 * (no por episodio): un tercero (familiar/acudiente/autorizado) firma que
 * recibió el resultado. Un paciente puede tener varias constancias.
 */

/** Input para crear + firmar la constancia (POST /api/pathology/sign). */
export const SignPathologyDeliverySchema = z.object({
  pacienteId: PatientIdSchema,
  /** Nombre del tipo de patología del catálogo, o texto libre cuando es "Otro". */
  patologia: z.string().min(2).max(160),
  nombreReceptor: z.string().min(2).max(120),
  parentescoReceptor: z.string().min(2).max(80),
  documentoReceptor: z.string().min(3).max(30),
  /** Firma del receptor — dataURL PNG capturado en tablet. */
  firmaReceptor: z.string().min(50),
});
export type SignPathologyDeliveryInput = z.infer<typeof SignPathologyDeliverySchema>;

/** DTO completo de una constancia firmada. */
export const PathologyDeliverySchema = z.object({
  id: z.string().uuid(),
  pacienteId: PatientIdSchema,
  pacienteNombre: z.string(),
  pacienteDocumento: z.string(),
  patologia: z.string(),
  nombreReceptor: z.string(),
  parentescoReceptor: z.string(),
  documentoReceptor: z.string(),
  firmaReceptor: z.string(),
  firmadoEn: z.string().datetime(),
});
export type PathologyDelivery = z.infer<typeof PathologyDeliverySchema>;

/** Ítem de la lista de constancias (sin la firma, para tablas). */
export const PathologyDeliveryListItemSchema = PathologyDeliverySchema.omit({
  firmaReceptor: true,
});
export type PathologyDeliveryListItem = z.infer<typeof PathologyDeliveryListItemSchema>;

export const PathologyListResponseSchema = z.object({
  items: z.array(PathologyDeliveryListItemSchema),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().min(0),
});
export type PathologyListResponse = z.infer<typeof PathologyListResponseSchema>;

/** Filtros aceptados por GET /api/pathology. */
export const PathologyListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  pacienteId: PatientIdSchema.optional(),
  q: z.string().max(120).optional(),
  desde: z.string().date().optional(),
  hasta: z.string().date().optional(),
});
export type PathologyListQuery = z.infer<typeof PathologyListQuerySchema>;
